import { useState, useEffect } from "react";
import { contractAPI, brandPricesAPI } from "../utils/api";

export default function BrandContractPage({ onAskAI }) {
  const [activeSubTab, setActiveSubTab] = useState("contract");
  const [area, setArea] = useState(1500);
  const [city, setCity] = useState("Nagpur");
  const [quality, setQuality] = useState("standard");

  const [contractData, setContractData] = useState(null);
  const [brandData, setBrandData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleContractCompare = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await contractAPI.compare({
        area_sqft: parseFloat(area),
        city,
        quality
      });
      if (res.success) setContractData(res.data);
    } catch (err) {
      setError(err.message || "Comparison failed");
    } finally {
      setLoading(false);
    }
  };

  const handleFetchBrandPrices = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await brandPricesAPI.getPrices(city);
      if (res.success) setBrandData(res.data);
    } catch (err) {
      setError(err.message || "Fetch prices failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleContractCompare();
    handleFetchBrandPrices();
  }, [city]);

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">🏷️ Brand Material Rates & Contract Comparator</h1>
          <p className="page-subtitle">
            Compare Turnkey vs Item-Rate Labor vs Self-Supervision costs, and audit top Indian material brand rates.
          </p>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            className={`btn ${activeSubTab === "contract" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setActiveSubTab("contract")}
          >
            🤝 Contract Models
          </button>
          <button
            className={`btn ${activeSubTab === "brands" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setActiveSubTab("brands")}
          >
            🏷️ Material Brand Rates
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {/* Contract Models Comparison Tab */}
      {activeSubTab === "contract" && (
        <div>
          {/* Controls */}
          <div className="card form-card" style={{ marginBottom: "24px" }}>
            <h3 className="card-title">Contract Comparison Setup</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Plot / Built-up Area (sq ft)</label>
                <input
                  type="number"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  className="input-field"
                />
              </div>

              <div className="form-group">
                <label>City of Construction</label>
                <select value={city} onChange={(e) => setCity(e.target.value)} className="select-input">
                  <option value="Nagpur">Nagpur (Tier 2)</option>
                  <option value="Mumbai">Mumbai (Tier 1)</option>
                  <option value="Pune">Pune (Tier 1)</option>
                  <option value="Delhi">Delhi / NCR (Tier 1)</option>
                  <option value="Bengaluru">Bengaluru (Tier 1)</option>
                  <option value="Indore">Indore (Tier 2)</option>
                  <option value="Jaipur">Jaipur (Tier 2)</option>
                  <option value="Nashik">Nashik (Tier 2)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Specification Quality</label>
                <select value={quality} onChange={(e) => setQuality(e.target.value)} className="select-input">
                  <option value="economy">Economy / Budget</option>
                  <option value="standard">Standard Quality</option>
                  <option value="premium">Premium Quality</option>
                  <option value="luxury">Ultra Luxury</option>
                </select>
              </div>
            </div>

            <button onClick={handleContractCompare} disabled={loading} className="btn btn-primary btn-full">
              {loading ? "Calculating Models..." : "🤝 Calculate Contract Model Comparison"}
            </button>
          </div>

          {/* Model Comparison Cards */}
          {contractData && (
            <div className="features-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
              {contractData.models.map((model) => (
                <div key={model.id} className="card" style={{ padding: "24px", background: "var(--white)", borderTop: model.id === "item_rate" ? "4px solid var(--green)" : "4px solid var(--saffron)" }}>
                  {model.id === "item_rate" && (
                    <div style={{ background: "var(--green-light)", color: "var(--green)", border: "1px solid rgba(16, 185, 129, 0.3)", padding: "4px 10px", borderRadius: "4px", fontSize: "11px", fontWeight: "bold", width: "fit-content", marginBottom: "12px" }}>
                      ⭐ RECOMMENDED FOR SAVINGS
                    </div>
                  )}

                  <h3 style={{ margin: "0 0 8px 0", color: "var(--charcoal)", fontSize: "18px", fontWeight: "700" }}>{model.name}</h3>
                  <div style={{ fontSize: "13px", color: "var(--slate)", marginBottom: "16px", lineHeight: "1.4" }}>{model.recommended_for}</div>

                  <div style={{ background: "var(--cream)", padding: "16px", borderRadius: "8px", marginBottom: "16px", border: "1px solid var(--border)" }}>
                    <div style={{ fontSize: "12px", color: "var(--slate)", fontWeight: "500" }}>Total Cost ({area} sq ft)</div>
                    <div style={{ fontSize: "26px", fontWeight: "800", color: "var(--saffron)", margin: "4px 0" }}>
                      ₹{model.total_cost_lakhs} Lakhs
                    </div>
                    <div style={{ fontSize: "13px", color: "var(--charcoal)", fontWeight: "600" }}>
                      ₹{model.cost_per_sqft} / sq ft
                    </div>
                    {model.savings_vs_turnkey && (
                      <div style={{ color: "var(--green)", fontSize: "13px", fontWeight: "bold", marginTop: "8px" }}>
                        🎉 Saves ₹{(model.savings_vs_turnkey / 100000).toFixed(2)} Lakhs vs Turnkey!
                      </div>
                    )}
                  </div>

                  <div style={{ fontSize: "13px", color: "var(--charcoal)", marginBottom: "16px", lineHeight: "1.6" }}>
                    <strong>Hassle Score:</strong> {model.hassle_rating}<br />
                    <strong>Risk Level:</strong> {model.risk_level}<br />
                    <strong>Quality Control:</strong> {model.control_over_quality}
                  </div>

                  <div style={{ borderTop: "1px solid var(--border)", paddingTop: "14px" }}>
                    <div style={{ color: "var(--green)", fontSize: "12px", fontWeight: "bold", marginBottom: "8px" }}>PROS:</div>
                    <ul style={{ margin: "0 0 14px 0", paddingLeft: "18px", fontSize: "13px", color: "var(--charcoal)", lineHeight: "1.5" }}>
                      {model.pros.map((p, i) => <li key={i}>{p}</li>)}
                    </ul>

                    <div style={{ color: "var(--red)", fontSize: "12px", fontWeight: "bold", margin: "12px 0 8px 0" }}>CONS:</div>
                    <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "13px", color: "var(--charcoal)", lineHeight: "1.5" }}>
                      {model.cons.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Brand Prices Tab */}
      {activeSubTab === "brands" && brandData && (
        <div>
          <div className="card" style={{ marginBottom: "20px", padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--white)" }}>
            <div>
              <h3 style={{ margin: 0, color: "var(--charcoal)", fontSize: "18px" }}>📍 Market Rates Matrix: {brandData.city} ({brandData.tier})</h3>
              <div style={{ fontSize: "12px", color: "var(--slate)" }}>{brandData.last_updated}</div>
            </div>

            <select value={city} onChange={(e) => setCity(e.target.value)} className="select-input" style={{ width: "auto" }}>
              <option value="Nagpur">Nagpur</option>
              <option value="Mumbai">Mumbai</option>
              <option value="Pune">Pune</option>
              <option value="Delhi">Delhi / NCR</option>
              <option value="Bengaluru">Bengaluru</option>
              <option value="Indore">Indore</option>
            </select>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {brandData.categories.map((cat, idx) => (
              <div key={idx} className="card" style={{ padding: "20px", background: "var(--white)" }}>
                <h3 style={{ color: "var(--saffron)", marginBottom: "14px", fontSize: "17px" }}>{cat.category}</h3>
                <div className="table-responsive">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Brand Name</th>
                        <th>Grade / Spec</th>
                        <th>Benchmark Price ({cat.unit})</th>
                        <th>Quality Rating</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cat.brands.map((b, i) => (
                        <tr key={i}>
                          <td><strong style={{ color: "var(--charcoal)" }}>{b.brand}</strong></td>
                          <td style={{ color: "var(--slate)" }}>{b.grade}</td>
                          <td><strong style={{ color: "var(--saffron)", fontSize: "15px" }}>₹{b.price_inr}</strong></td>
                          <td style={{ color: "var(--charcoal)" }}>{b.rating}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
